import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Button,
  InputGroup,
  Form,
  Row,
  Col,
  Container,
  Spinner,
  Badge,
} from 'react-bootstrap';
import { FaFileExcel, FaSearch, FaDollarSign, FaTimes } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import { authAxios } from '../utils/reportUtils';

// Helper function to get current month date range
const getCurrentMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  // First day of current month
  const firstDay = new Date(year, month, 1);
  const fromDate = firstDay.toISOString().split('T')[0];

  // Last day of current month
  const lastDay = new Date(year, month + 1, 0);
  const toDate = lastDay.toISOString().split('T')[0];

  return { fromDate, toDate };
};

const CollectionsReport = () => {
  const { fromDate: defaultFromDate, toDate: defaultToDate } =
    getCurrentMonthRange();
  // Default to today
  const today = new Date().toISOString().slice(0, 10);
  const [allCollections, setAllCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const abortControllerRef = useRef(null);

  // Fetch all collections
  const fetchAllCollections = async (fromDate = null, toDate = null) => {
    // Abort any previous fetch and create a new controller
    try {
      abortControllerRef.current?.abort();
    } catch (e) { }
    abortControllerRef.current = new AbortController();
    setLoading(true);

    try {
      const userShowroomId = localStorage.getItem('userShowroomId');
      const params = {
        showroom_id: userShowroomId,
      };
      const from = fromDate !== null ? fromDate : dateFrom;
      const to = toDate !== null ? toDate : dateTo;

      // Add date filters if provided
      if (from) {
        params.start_date = from;
      }
      if (to) {
        params.end_date = to;
      }

      const res = await authAxios.get('/collections', { params, signal: abortControllerRef.current?.signal });
      // Handle new API response structure: { success: true, data: [...] }
      const collectionsData =
        res.data?.success && Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : Array.isArray(res.data)
              ? res.data
              : [];

      setAllCollections(collectionsData);
    } catch (err) {
      console.error('Error fetching collections:', err);
      setAllCollections([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchAllCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format date and time
  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    try {
      const date = new Date(dateTime);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const time = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
      return `${day}-${month}-${year}, ${time}`;
    } catch {
      return 'Invalid Date';
    }
  };

  // Get status variant and color for badge
  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'collected':
      case 'completed':
        return 'success';
      case 'pending':
      case 'requested':
        return 'warning';
      case 'created':
        return 'primary';
      case 'assigned':
      case 'assigned_to_driver':
      case 'in_progress':
        return 'info';
      case 'cancelled':
      case 'rejected':
      case 'declined':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Get custom color for status badge
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'collected':
      case 'completed':
        return { backgroundColor: '#28a745', color: '#fff' }; // Green
      case 'pending':
      case 'requested':
        return { backgroundColor: '#ffc107', color: '#000' }; // Yellow/Orange
      case 'created':
        return { backgroundColor: '#007bff', color: '#fff' }; // Blue Primary
      case 'assigned':
      case 'assigned_to_driver':
      case 'in_progress':
        return { backgroundColor: '#17a2b8', color: '#fff' }; // Cyan/Blue
      case 'cancelled':
      case 'rejected':
      case 'declined':
        return { backgroundColor: '#dc3545', color: '#fff' }; // Red
      default:
        return { backgroundColor: '#6c757d', color: '#fff' }; // Gray
    }
  };

  // Client-side filtering
  const filteredCollections = useMemo(() => {
    let filtered = allCollections;

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.code?.toLowerCase().includes(searchLower) ||
          c.id?.toString().includes(search) ||
          c.customer_name?.toLowerCase().includes(searchLower) ||
          c.showroom_name?.toLowerCase().includes(searchLower) ||
          c.driver_name?.toLowerCase().includes(searchLower) ||
          c.method?.toLowerCase().includes(searchLower) ||
          c.amount?.toString().includes(search) ||
          c.status?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [allCollections, search]);

  // Handle search button click
  const handleSearch = () => {
    fetchAllCollections();
  };

  // Clear all filters and fetch
  const handleClearFilters = () => {
    setSearch('');
    // Reset to today and fetch
    const t = new Date().toISOString().slice(0, 10);
    setDateFrom(t);
    setDateTo(t);
    fetchAllCollections(t, t);
  };

  // Export to Excel
  const handleExportToExcel = () => {
    if (!filteredCollections || filteredCollections.length === 0) {
      alert('No collection data to export.');
      return;
    }

    const exportData = filteredCollections.map((c, idx) => ({
      'S.No': idx + 1,
      'Collection No': c.code || `#${c.id}` || 'N/A',
      'Showroom Name': c.showroom_name || 'N/A',
      'Customer Name': c.customer_name || 'N/A',
      Amount: c.amount || 0,
      'Mode of Payment': c.method || 'N/A',
      'Driver Name': c.driver_name || 'N/A',
      'Created Date and Time': formatDateTime(c.created_at),
      'Assigned Date and Time': formatDateTime(c.assigned_at),
      'Collected Date and Time': formatDateTime(c.collected_at),
      Status: c.status || 'N/A',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Collections');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    saveAs(blob, `collections_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Custom Loading Component
  const CustomLoadingIndicator = (
    <div className='p-3 text-center'>
      <Spinner
        animation='border'
        role='status'
        className='me-2'
        variant='primary'
      />
      <span className='text-primary'>Loading Collections...</span>
    </div>
  );

  // Custom No Data Component
  const CustomNoDataComponent = (
    <div className='p-4 text-center text-muted'>
      <FaDollarSign className='mb-2' size={32} />
      <h5 className='mt-2'>No Collections Found</h5>
      <p className='mb-0'>
        There are currently no collections matching your search criteria.
      </p>
    </div>
  );

  const columns = [
    {
      name: '#',
      selector: (row, index) => index + 1,
      width: '60px',
    },
    {
      name: 'Collection No',
      selector: (row) => row.code || row.id,
      sortable: true,
      cell: (row) => <strong>{row.code || `#${row.id}`}</strong>,
    },
    {
      name: 'Showroom',
      selector: (row) => row.showroom_name || 'N/A',
      sortable: true,
    },
    {
      name: 'Customer Name',
      selector: (row) => row.customer_name || 'N/A',
      sortable: true,
    },
    {
      name: 'Amount',
      selector: (row) => row.amount || 0,
      sortable: true,
      cell: (row) => (
        <strong className='text-success'>AED {row.amount || '0.00'}</strong>
      ),
    },
    {
      name: 'Mode of Payment',
      selector: (row) => row.method || 'N/A',
      sortable: true,
      cell: (row) => {
        const method = row.method || 'N/A';
        return method
          .split('_')
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(' ');
      },
    },
    {
      name: 'Driver Name',
      selector: (row) => row.driver_name || 'N/A',
      sortable: true,
    },
    {
      name: 'Created Date and Time',
      selector: (row) => row.created_at,
      sortable: true,
      cell: (row) => <small>{formatDateTime(row.created_at)}</small>,
    },
    {
      name: 'Assigned Date and Time',
      selector: (row) => row.assigned_at,
      sortable: true,
      cell: (row) => <small>{formatDateTime(row.assigned_at)}</small>,
    },
    {
      name: 'Collected Date and Time',
      selector: (row) => row.collected_at,
      sortable: true,
      cell: (row) => <small>{formatDateTime(row.collected_at)}</small>,
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        const status = row.status || 'N/A';
        const statusText = status.replace(/_/g, ' ');
        const colorStyle = getStatusColor(status);
        return (
          <Badge
            bg={getStatusVariant(status)}
            style={{
              backgroundColor: colorStyle.backgroundColor,
              color: colorStyle.color,
              padding: '6px 12px',
              borderRadius: '4px',
              fontWeight: '500',
            }}
          >
            {statusText.charAt(0).toUpperCase() +
              statusText.slice(1).toLowerCase()}
          </Badge>
        );
      },
    },
  ];

  return (
    <Container fluid className='p-4'>
      <h3 style={{ textAlign: 'center' }}>Collections Report</h3>

      {/* Filters */}
      <div className='row justify-content-between align-items-center mt-3 mb-3'>
        <div className='col-md-9 col-lg-9 col-sm-12 align-items-center py-3 gap-2'>
          <Row className='g-2'>
            <Col xs='auto'>
              <InputGroup style={{ width: '300px' }}>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type='text'
                  placeholder='Search Collections...'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                />
              </InputGroup>
            </Col>
            <Col xs='auto'>
              <Form.Control
                type='date'
                placeholder='From Date'
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ width: '150px' }}
              />
            </Col>
            <Col xs='auto'>
              <Form.Control
                type='date'
                placeholder='To Date'
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ width: '150px' }}
              />
            </Col>
            <Col xs='auto'>
              <Button size='sm' variant='primary' onClick={handleSearch}>
                <FaSearch /> Search
              </Button>
            </Col>
            <Col xs='auto'>
              <Button
                size='sm'
                variant='secondary'
                onClick={handleClearFilters}
              >
                <FaTimes /> Clear
              </Button>
            </Col>
          </Row>
        </div>
        <div className='col-md-3 col-lg-3 col-sm-12 text-end'>
          <Button size='sm' variant='success' onClick={handleExportToExcel}>
            <FaFileExcel /> Export to Excel
          </Button>
        </div>
      </div>

      {/* Collections Table */}
      <div style={{ maxHeight: '54vh', position: 'relative' }}>
        {filteredCollections.length > 0 && (
          <div
            className='text-end p-2'
            style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #dee2e6',
            }}
          >
            <small className='text-muted'>
              Showing <strong>{filteredCollections.length}</strong>{' '}
              collection(s)
            </small>
          </div>
        )}
        <DataTable
          columns={columns}
          data={filteredCollections}
          pagination={false}
          highlightOnHover
          striped
          dense
          persistTableHead
          fixedHeader
          fixedHeaderScrollHeight='54vh'
          noTableHead={false}
          customStyles={{
            table: {
              style: {
                tableLayout: 'auto',
                width: '100%',
                maxWidth: '100%',
              },
            },
            tableWrapper: {
              style: {
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
              },
            },
            headRow: {
              style: {
                backgroundColor: '#f8f9fa',
                borderBottomStyle: 'solid',
                borderBottomWidth: '2px',
                borderBottomColor: '#dee2e6',
              },
            },
            headCells: {
              style: {
                fontSize: '14px',
                fontWeight: '600',
                paddingLeft: '8px',
                paddingRight: '8px',
              },
            },
            cells: {
              style: {
                wordBreak: 'break-word',
                whiteSpace: 'normal',
              },
            },
          }}
          progressPending={loading}
          progressComponent={CustomLoadingIndicator}
          noDataComponent={CustomNoDataComponent}
        />
      </div>
    </Container>
  );
};

export default CollectionsReport;
